import React, { PropsWithChildren } from 'react'
import { DateOption } from '../../legacy-design-system/date-picker/shared'

export interface SourceNameFormState {
  name: string
  cidrBlocks: string[]
  startOption: DateOption
  startDate: Date | undefined
  expirationOption: DateOption
  expirationDate: Date | undefined
  hasFormChanged: boolean
}

export type SourceNameFormAction =
  | { type: 'EDIT_NAME'; payload: string }
  | {
      type: 'EDIT_DATE_OPTION'
      payload:
        | { startOption: DateOption; startDate?: Date }
        | { expirationOption: DateOption; expirationDate?: Date }
    }
  | {
      type: 'EDIT_DATE'
      payload:
        | { startDate: Date | undefined }
        | { expirationDate: Date | undefined }
    }
  | { type: 'ADD_CIDR_BLOCK'; payload: string }
  | { type: 'EDIT_CIDR_BLOCK'; payload: { value: string; index: number } }
  | { type: 'REMOVE_CIDR_BLOCK'; payload: number }

export const sourceNameFormReducer = (
  state: SourceNameFormState,
  action: SourceNameFormAction
) => {
  switch (action.type) {
    case 'EDIT_NAME':
      return { ...state, name: action.payload, hasFormChanged: true }
    case 'EDIT_DATE_OPTION':
      return { ...state, ...action.payload, hasFormChanged: true }
    case 'EDIT_DATE':
      return { ...state, ...action.payload, hasFormChanged: true }
    case 'ADD_CIDR_BLOCK':
      return {
        ...state,
        cidrBlocks: [...state.cidrBlocks, action.payload],
        hasFormChanged: true
      }
    case 'EDIT_CIDR_BLOCK':
      return {
        ...state,
        cidrBlocks: state.cidrBlocks.map((cidrBlock, mapIndex) =>
          action.payload.index === mapIndex ? action.payload.value : cidrBlock
        ),
        hasFormChanged: true
      }
    case 'REMOVE_CIDR_BLOCK':
      return {
        ...state,
        cidrBlocks: state.cidrBlocks.filter(
          (_, index) => index !== action.payload
        ),
        hasFormChanged: true
      }
  }
}

export const setInitialState = ({ cidrBlocks, name, start, expiration }) => ({
  cidrBlocks,
  name,
  startOption: start ? DateOption.CUSTOM : DateOption.NOW,
  startDate: start ? new Date(start) : undefined,
  expirationOption: expiration ? DateOption.CUSTOM : DateOption.NEVER,
  expirationDate: expiration ? new Date(expiration) : undefined,
  hasFormChanged: false
})

interface SourceNameContext {
  state: SourceNameFormState
  sourceName: SourceNameForProvider
  dispatch: React.Dispatch<SourceNameFormAction>
}

export interface SourceNameForProvider {
  id: string
  name: string
  start: string | undefined
  expiration: string | undefined
  cidrBlocks: string[]
}

const SourceNameFormContext = React.createContext<
  SourceNameContext | undefined
>(undefined)

export const SourceNameFormProvider: React.FC<
  PropsWithChildren<{
    sourceName: SourceNameForProvider
  }>
> = ({ children, sourceName }) => {
  const [state, dispatch] = React.useReducer(
    sourceNameFormReducer,
    sourceName,
    setInitialState
  )

  const value = React.useMemo(
    () => ({ state, dispatch, sourceName }),
    [state, dispatch, sourceName]
  )

  return (
    <SourceNameFormContext.Provider value={value}>
      {children}
    </SourceNameFormContext.Provider>
  )
}

export const useSourceNameFormContext = () => {
  const context = React.useContext(SourceNameFormContext)
  if (!context) {
    throw new Error(
      'useSourceNameFormContext must be used within a SourceNameFormProvider'
    )
  }
  return context
}
